import { useState } from "react";
import ReactDiffViewer from 'react-diff-viewer';
import { fetchSubmitEnrollmentSpares } from "../../utils/apiUtils";
import ReactJson from "react-json-view";
import { sortByKeys } from "../../utils/commonUtils";
import DatePicker from "react-datepicker";
import Modal from "../modal/Modal";

const cardStyle = {
    backgroundColor: 'var(--white)',
    borderRadius: '.6rem',
    boxShadow: 'rgba(0, 0, 0, 0.05) 0px 0px 0px 1px',
    padding: '1rem 1.2rem',
};

const sectionTitleStyle = {
    fontSize: '1rem',
    fontWeight: 600,
    paddingBottom: '.6rem',
    color: 'var(--darkerGrey)',
};

const fieldRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '.3rem 0',
    borderBottom: '1px solid var(--lightestGrey)',
    gap: '1rem',
};

const fieldLabelStyle = {
    color: 'var(--grey)',
    fontSize: '.9rem',
    minWidth: '140px',
    flexShrink: 0,
};

const fieldValueStyle = {
    fontSize: '.9rem',
    fontWeight: 500,
    color: 'var(--darkerGrey)',
    textAlign: 'right',
    wordBreak: 'break-word',
};

const isPrimitive = (val) => val === null || val === undefined || typeof val !== 'object';

const formatLabel = (key) =>
    key.replace(/([A-Z])/g, ' $1')
        .replace(/[_-]/g, ' ')
        .replace(/^./, s => s.toUpperCase())
        .trim();

const FieldValue = ({ label, value, depth = 0, defaultOpen }) => {
    const shouldDefaultOpen = defaultOpen !== undefined ? defaultOpen : depth < 1;
    const [expanded, setExpanded] = useState(shouldDefaultOpen);

    if (value === undefined || value === null || value === '') return null;

    if (isPrimitive(value)) {
        return (
            <div style={fieldRowStyle}>
                <span style={fieldLabelStyle}>{label}</span>
                <span style={fieldValueStyle}>{String(value)}</span>
            </div>
        );
    }

    if (Array.isArray(value)) {
        if (value.length === 0) return null;

        if (value.every(isPrimitive)) {
            return (
                <div style={fieldRowStyle}>
                    <span style={fieldLabelStyle}>{label}</span>
                    <span style={fieldValueStyle}>{value.filter(v => v !== null && v !== undefined).join(', ')}</span>
                </div>
            );
        }

        return (
            <div style={{ padding: '.2rem 0' }}>
                <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '.5rem', padding: '.3rem 0' }}
                    onClick={() => setExpanded(!expanded)}>
                    <span style={{ ...fieldLabelStyle, fontWeight: 600, color: 'var(--darkerGrey)' }}>
                        {label}
                    </span>
                    <span style={{ fontSize: '.75rem', color: 'var(--grey)', backgroundColor: 'var(--lightestGrey)', borderRadius: '1rem', padding: '0 .5rem' }}>
                        {value.length}
                    </span>
                    <span style={{ fontSize: '.7rem', color: 'var(--grey)' }}>{expanded ? '▾' : '▸'}</span>
                </div>
                {expanded && (
                    <div style={{ maxHeight: depth > 0 ? '400px' : undefined, overflowY: depth > 0 ? 'auto' : undefined }}
                        className={depth > 0 ? 'scrollable-form' : undefined}>
                        {value.map((item, i) => (
                            <div key={i} style={{
                                paddingLeft: '.8rem',
                                borderLeft: '2px solid var(--lighterGrey)',
                                marginTop: '.3rem',
                                marginBottom: '.3rem',
                            }}>
                                {isPrimitive(item) ? (
                                    <span style={fieldValueStyle}>{String(item)}</span>
                                ) : (
                                    <AutoFields data={item} depth={depth + 1} />
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div style={{ padding: '.2rem 0' }}>
            <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '.5rem', padding: '.3rem 0' }}
                onClick={() => setExpanded(!expanded)}>
                <span style={{ ...fieldLabelStyle, fontWeight: 600, color: 'var(--darkerGrey)' }}>
                    {label}
                </span>
                <span style={{ fontSize: '.7rem', color: 'var(--grey)' }}>{expanded ? '▾' : '▸'}</span>
            </div>
            {expanded && (
                <div style={{
                    paddingLeft: '.8rem',
                    borderLeft: '2px solid var(--lighterGrey)',
                }}>
                    <AutoFields data={value} depth={depth + 1} />
                </div>
            )}
        </div>
    );
};

const sortEntries = (entries) =>
    [...entries].sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

const AutoFields = ({ data, depth = 0 }) => {
    if (!data || typeof data !== 'object') return null;

    const entries = Array.isArray(data)
        ? data.map((item, i) => [String(i), item])
        : Object.entries(data);

    const primitives = sortEntries(entries.filter(([, v]) => isPrimitive(v)));
    const complex = sortEntries(entries.filter(([, v]) => !isPrimitive(v)));

    return (
        <>
            {primitives.map(([key, val]) => (
                <FieldValue key={key} label={formatLabel(key)} value={val} depth={depth} />
            ))}
            {complex.map(([key, val]) => (
                <FieldValue key={key} label={formatLabel(key)} value={val} depth={depth} />
            ))}
        </>
    );
};

const Card = ({ title, children, defaultOpen = true, count }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div style={cardStyle}>
            <div style={{ ...sectionTitleStyle, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                onClick={() => setOpen(!open)}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                    {title}
                    {count !== undefined && (
                        <span style={{ fontSize: '.75rem', color: 'var(--grey)', backgroundColor: 'var(--lightestGrey)', borderRadius: '1rem', padding: '0 .5rem', fontWeight: 500 }}>
                            {count}
                        </span>
                    )}
                </span>
                <span style={{ fontSize: '.8rem', color: 'var(--grey)' }}>{open ? '▾' : '▸'}</span>
            </div>
            {open && children}
        </div>
    );
};

const Badge = ({ children, color = 'var(--blue)', bg = 'var(--lightestBlue)' }) => (
    <span style={{
        display: 'inline-block', padding: '.2rem .8rem', borderRadius: '1rem',
        fontSize: '.8rem', fontWeight: 600, backgroundColor: bg, color, whiteSpace: 'nowrap',
    }}>{children}</span>
);

const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
    gap: '1rem',
    padding: '.6rem 0',
};

const DataView = ({ data, label }) => {
    if (!data || typeof data !== 'object') return null;

    const entries = Object.entries(data);
    const primitiveEntries = sortEntries(entries.filter(([, v]) => isPrimitive(v)));
    const objectEntries = sortEntries(entries.filter(([, v]) => v && typeof v === 'object' && !Array.isArray(v)));
    const arrayEntries = sortEntries(entries.filter(([, v]) => Array.isArray(v)));

    return (
        <div>
            {label && (
                <div style={{ display: 'flex', gap: '.5rem', paddingBottom: '.4rem', flexWrap: 'wrap' }}>
                    {data.activationStatus && (
                        <Badge color={data.activationStatus === 'Activated' ? 'var(--green)' : 'var(--orange)'}
                            bg={data.activationStatus === 'Activated' ? 'var(--lightGreen)' : 'var(--lightOragne)'}>
                            {data.activationStatus}
                        </Badge>
                    )}
                    {data.eligibilityStatus && (
                        <Badge color={data.eligibilityStatus === 'Active' ? 'var(--green)' : 'var(--red)'}
                            bg={data.eligibilityStatus === 'Active' ? 'var(--lightGreen)' : 'var(--lightRed)'}>
                            {data.eligibilityStatus}
                        </Badge>
                    )}
                    {data.mfaEnabled && <Badge color="var(--blue)" bg="var(--lightestBlue)">MFA</Badge>}
                </div>
            )}

            <div style={gridStyle}>
                {primitiveEntries.length > 0 && (
                    <Card title={label ? 'General' : 'Properties'}>
                        {primitiveEntries.map(([key, val]) => (
                            <FieldValue key={key} label={formatLabel(key)} value={val} depth={0} />
                        ))}
                    </Card>
                )}

                {objectEntries.map(([key, val]) => (
                    <Card key={key} title={formatLabel(key)} defaultOpen={true}>
                        <AutoFields data={val} depth={0} />
                    </Card>
                ))}

                {arrayEntries.map(([key, val]) => {
                    if (!val || val.length === 0) return null;

                    if (val.every(isPrimitive)) {
                        return (
                            <Card key={key} title={formatLabel(key)} count={val.length}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.4rem' }}>
                                    {val.map((item, i) => (
                                        <Badge key={i}>{String(item)}</Badge>
                                    ))}
                                </div>
                            </Card>
                        );
                    }

                    return (
                        <Card key={key} title={formatLabel(key)} count={val.length} defaultOpen={val.length <= 5}>
                            <div style={{ maxHeight: '500px', overflowY: 'auto' }} className="scrollable-form">
                                {val.map((item, i) => (
                                    <div key={i} style={{
                                        padding: '.5rem .6rem',
                                        borderRadius: '.4rem',
                                        backgroundColor: i % 2 === 0 ? 'var(--lightestGrey)' : 'transparent',
                                        marginBottom: '.2rem',
                                    }}>
                                        {isPrimitive(item) ? (
                                            <span style={fieldValueStyle}>{String(item)}</span>
                                        ) : (
                                            <AutoFields data={item} depth={1} />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </Card>
                    );
                })}
            </div>
        </div>
    );
};

const MemberDetails = ({ data, dynamDBMembersData, autoRefreshEnabled, refetch }) => {
    const [viewMode, setViewMode] = useState('raw');
    const [dynamoViewMode, setDynamoViewMode] = useState('raw');
    const [viewDiff, setViewDiff] = useState(false);
    const [selectedDate, setSelectedDate] = useState();
    const [pcpUpdateResponse, setPcpUpdateResponse] = useState(null);
    const [isUpdatingPcp, setIsUpdatingPcp] = useState(false);

    const member = data?.items?.[0]?.memberExtract?.member;
    const hasMultipleDynamDBMembersByMemberUuid = dynamDBMembersData?.Items?.length > 1;

    const updatePcpInHrp = async (payload) => {
        setIsUpdatingPcp(true);
        try {
            const result = await fetchSubmitEnrollmentSpares(payload);
            setPcpUpdateResponse(result ?? {});
            await refetch();
        } catch (err) {
            setPcpUpdateResponse(err.cause && typeof err.cause === 'object'
                ? err.cause
                : { message: err.message });
        } finally {
            setIsUpdatingPcp(false);
        }
    };

    return (
        <div style={{ overflowY: 'scroll' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '.6rem 0' }}>
                <p style={sectionTitleStyle}>HCC Member Extract</p>
                <div className="switch">
                    <p className={`option ${viewMode === 'formatted' ? 'active' : ''}`}
                        onClick={() => setViewMode('formatted')}>Formatted</p>
                    <p className={`option ${viewMode === 'raw' ? 'active' : ''}`}
                        onClick={() => setViewMode('raw')}>Raw JSON</p>
                </div>
            </div>

            {viewMode === 'raw' ? (
                <ReactJson
                    style={{ padding: "1rem" }}
                    src={data}
                    name={false}
                    collapsed={4}
                    collapseStringsAfterLength={150}
                    displayDataTypes={false}
                    displayObjectSize={false}
                    sortKeys={false}
                />
            ) : (
                <DataView data={member} />
            )}

            {dynamDBMembersData?.Items?.length > 0 && <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.2rem 0 .6rem 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <p className="title">DynamoDB</p>
                        {hasMultipleDynamDBMembersByMemberUuid && (
                            <p className="clickable-link" onClick={() => setViewDiff(!viewDiff)} style={{ fontSize: '.9rem' }}>
                                {viewDiff ? 'Hide Diff' : 'Show Diff'}
                            </p>
                        )}
                    </div>
                    <div className="switch">
                        <p className={`option ${dynamoViewMode === 'formatted' ? 'active' : ''}`}
                            onClick={() => setDynamoViewMode('formatted')}>Formatted</p>
                        <p className={`option ${dynamoViewMode === 'raw' ? 'active' : ''}`}
                            onClick={() => setDynamoViewMode('raw')}>Raw JSON</p>
                    </div>
                </div>

                {viewDiff && hasMultipleDynamDBMembersByMemberUuid ? (
                    <ReactDiffViewer
                        oldValue={JSON.stringify(sortByKeys(dynamDBMembersData?.Items[0]), null, 4)}
                        newValue={JSON.stringify(sortByKeys(dynamDBMembersData?.Items[dynamDBMembersData.Items.length - 1]), null, 4)}
                        disableWordDiff={true}
                        splitView={true}
                    />
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        {dynamDBMembersData?.Items?.map((item, i) => {
                            let updatePcpPayload = null;
                            if (item.tableVersion === 'HRP_1' && item.pcpProviderId && item.pcpLocationId) {
                                updatePcpPayload = {
                                    member: {
                                        membershipIdentifier: item.memberKey,
                                        providerSelections: {
                                            providerSelection: {
                                                providerDateRanges: {
                                                    startDate: selectedDate?.toISOString()?.slice(0, 10),
                                                    providerMatch: {
                                                        practitioner: {
                                                            hccIdentificationNumber: item.pcpProviderId || item.pcpLocationId.slice(0, 8)
                                                        },
                                                        practitionerRole: {
                                                            roleName: `${item.pcpProviderId || item.pcpLocationId.slice(0, 8)}_${item.pcpLocationId}`,
                                                        }
                                                    },
                                                }
                                            }
                                        }
                                    }
                                };
                            }

                            return (
                                <div key={item.memberUuid + '_' + item.tableVersion + '_' + i}>
                                    {dynamoViewMode === 'raw' ? (
                                        <>
                                            <p style={{ fontSize: '1rem', fontWeight: 600, padding: '.6rem 0' }}>{item.tableVersion}</p>
                                            <ReactJson
                                                style={{ padding: "1rem" }}
                                                src={item}
                                                name={false}
                                                collapsed={2}
                                                collapseStringsAfterLength={150}
                                                displayDataTypes={false}
                                                displayObjectSize={false}
                                                sortKeys={true}
                                            />
                                        </>
                                    ) : (
                                        <DataView data={item} label={item.tableVersion} />
                                    )}

                                    {updatePcpPayload && (
                                        <div style={{ paddingTop: '.8rem' }}>
                                            <p style={{ fontWeight: 600, padding: '.4rem 0', fontSize: '1rem' }}>Sync PCP in HRP from DynamoDB</p>
                                            <div style={{ display: 'flex', flexDirection: 'column', borderRadius: '.6rem', padding: '1rem', backgroundColor: 'var(--lightestGrey)' }}>
                                                <label style={{ padding: '.4rem 0' }}>PCP Start Date</label>
                                                <DatePicker
                                                    className="date-picker"
                                                    placeholderText="PCP startDate"
                                                    inline={false}
                                                    dateFormat={'yyyy-MM-dd'}
                                                    selected={selectedDate}
                                                    onChange={(date) => setSelectedDate(date)}
                                                />
                                                <br />
                                                <ReactJson
                                                    style={{ padding: "1rem" }}
                                                    src={updatePcpPayload}
                                                    name={false}
                                                    collapsed={5}
                                                    collapseStringsAfterLength={150}
                                                    displayDataTypes={false}
                                                    displayObjectSize={false}
                                                    sortKeys={false}
                                                />
                                                <button className="btn"
                                                    disabled={!selectedDate || isUpdatingPcp}
                                                    style={{ width: '100px' }}
                                                    onClick={() => updatePcpInHrp(updatePcpPayload)}>
                                                    {isUpdatingPcp ? 'Updating...' : 'Update'}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </>}

            <Modal isOpen={!!pcpUpdateResponse} onClose={() => setPcpUpdateResponse(null)}>
                <div style={{ padding: "1rem", width: "100%", height: "100%", overflowY: "scroll" }}>
                    <p className="title">PCP update response</p>
                    <ReactJson
                        style={{ padding: "1rem 0" }}
                        src={pcpUpdateResponse || {}}
                        name={false}
                        collapsed={2}
                        collapseStringsAfterLength={150}
                        displayDataTypes={false}
                        displayObjectSize={false}
                        sortKeys={false}
                    />
                </div>
            </Modal>
        </div>
    );
};

export default MemberDetails;